import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import AdminHeader from "../components/Admin/AdminHeader";
import "./adminJournal.css";

const AdminJournal = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    setLoading(true);
    setError("");

    const { data, error } = await supabase
      .from("journal_articles")
      .select(`
        id,
        title,
        slug,
        excerpt,
        cover_image,
        category,
        published,
        published_at,
        created_at,
        updated_at
      `)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error loading journal articles:", error);
      setError(error.message);
      setLoading(false);
      return;
    }

    setArticles(data || []);
    setLoading(false);
  };


  const getStoragePathFromUrl = (url) => {
  if (!url) {
    return null;
  }

  const marker =
    "/storage/v1/object/public/journal-images/";

  const index = url.indexOf(marker);

  if (index === -1) {
    return null;
  }

  return decodeURIComponent(
    url.substring(index + marker.length)
  );
};

const deleteStorageImage = async (url) => {
  const filePath = getStoragePathFromUrl(url);

  if (!filePath) {
    console.log(
      "No Supabase journal image to delete."
    );
    return true;
  }

  console.log(
    "Deleting article image:",
    filePath
  );

  const { data, error } = await supabase.storage
    .from("journal-images")
    .remove([filePath]);

  if (error) {
    console.error(
      "Unable to delete article image:",
      error
    );

    return false;
  }

  if (!data || data.length === 0) {
    console.warn(
      "Supabase returned 200 but no storage object was deleted:",
      filePath
    );

    return false;
  }

  console.log(
    "Article image deleted successfully:",
    data
  );

  return true;
};

const handleDelete = async (id) => {
  const confirmed = window.confirm(
    "Are you sure you want to delete this article?"
  );

  if (!confirmed) return;

  setError("");
  setSuccess("");
  setActionLoading(true);

  try {
    /*
     * 1. Get the article first so we know
     *    which cover image belongs to it.
     */
    const { data: article, error: fetchError } =
      await supabase
        .from("journal_articles")
        .select("id, title, cover_image")
        .eq("id", id)
        .single();

    if (fetchError) {
      throw fetchError;
    }

    if (!article) {
      throw new Error(
        "Article could not be found."
      );
    }

    /*
     * 2. Delete the article from the database.
     */
    const { data, error: deleteError } =
      await supabase
        .from("journal_articles")
        .delete()
        .eq("id", id)
        .select();

    if (deleteError) {
      throw deleteError;
    }

    if (!data || data.length === 0) {
      throw new Error(
        "You do not have permission to delete this article."
      );
    }

    /*
     * 3. Delete the associated cover image
     *    from Supabase Storage.
     */
    let imageCleanupWarning = false;

    if (article.cover_image) {
      const imageDeleted =
        await deleteStorageImage(
          article.cover_image
        );

      if (!imageDeleted) {
        imageCleanupWarning = true;
      }
    }

    /*
     * 4. Show appropriate message.
     */
    if (imageCleanupWarning) {
      setSuccess(
        "Article deleted, but its cover image could not be removed from Storage."
      );
    } else {
      setSuccess(
        "Article and its cover image deleted successfully."
      );
    }

    /*
     * 5. Refresh article list.
     */
    await loadArticles();

  } catch (err) {
    console.error(
      "Error deleting article:",
      err
    );

    setError(
      err?.message ||
        "Unable to delete the article. Please try again."
    );
  } finally {
    setActionLoading(false);
  }
};

  const handleTogglePublished = async (article) => {
    setError("");
    setSuccess("");
    setActionLoading(true);

    const newPublishedState = !article.published;

    const updateData = {
      published: newPublishedState,
      published_at: newPublishedState
        ? article.published_at || new Date().toISOString()
        : null,
    };

    const { data, error } = await supabase
      .from("journal_articles")
      .update(updateData)
      .eq("id", article.id)
      .select();

    if (error) {
      setError(error.message);
      setActionLoading(false);
      return;
    }

    if (!data || data.length === 0) {
      setError("You do not have permission to update this article.");
      setActionLoading(false);
      return;
    }

    setSuccess(
      newPublishedState
        ? "Article published successfully."
        : "Article moved to draft."
    );

    setActionLoading(false);

    await loadArticles();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not published";

    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="admin-page">

      <div className="admin-dashboard">

        {/* HEADER */}

        <AdminHeader
          backTo="/admin"
          backLabel="Dashboard"
        />


        {/* MAIN */}

        <main className="admin-main">

          <section className="admin-journal-title">

            <div>
              <span className="admin-page-eyebrow">
                CONTENT MANAGEMENT
              </span>

              <h1>Journal</h1>

              <div className="admin-title-divider"></div>

              <p>
                Create, edit and publish articles for the
                Sudhamshu Ayurveda Journal.
              </p>
            </div>

            <Link
              to="/admin/journal/new"
              className="admin-journal-add-btn"
            >
              + New Article
            </Link>

          </section>


          {/* MESSAGES */}

          {error && (
            <div className="admin-error admin-message">
              {error}
            </div>
          )}

          {success && (
            <div className="admin-success admin-message">
              {success}
            </div>
          )}


          {/* ARTICLE LIST */}

          <section className="admin-journal-card">

            <div className="admin-journal-card-header">

              <div>
                <span className="admin-section-label">
                  JOURNAL
                </span>

                <h2>Articles</h2>
              </div>

              <span className="admin-count">
                {articles.length}
                {articles.length === 1 ? " article" : " articles"}
              </span>

            </div>


            {loading ? (

              <div className="admin-loading">
                <div className="admin-spinner"></div>
                <p>Loading articles...</p>
              </div>

            ) : articles.length === 0 ? (

              <div className="admin-journal-empty">

                <div className="admin-journal-empty-icon">
                  ✎
                </div>

                <h3>No articles yet</h3>

                <p>
                  Create your first Journal article to
                  start publishing content.
                </p>

                <Link
                  to="/admin/journal/new"
                  className="admin-primary-btn admin-empty-btn"
                >
                  Create First Article
                </Link>

              </div>

            ) : (

              <div className="admin-article-list">

                {articles.map((article) => (

                  <article
                    key={article.id}
                    className="admin-article-row"
                  >

                    <div className="admin-article-main">

                      {article.cover_image ? (
                        <img
                          src={article.cover_image}
                          alt=""
                          className="admin-article-thumbnail"
                        />
                      ) : (
                        <div className="admin-article-thumbnail admin-article-thumbnail-placeholder">
                          ✎
                        </div>
                      )}

                      <div className="admin-article-info">

                        <div className="admin-article-meta">

                          <span className="admin-article-category">
                            {article.category || "AYURVEDA"}
                          </span>

                          <span
                            className={
                              article.published
                                ? "admin-status published"
                                : "admin-status draft"
                            }
                          >
                            {article.published
                              ? "Published"
                              : "Draft"}
                          </span>

                        </div>

                        <h3>
                          {article.title}
                        </h3>

                        {article.excerpt && (
                          <p>
                            {article.excerpt}
                          </p>
                        )}

                        <span className="admin-article-date">
                          {article.published
                            ? `Published ${formatDate(
                                article.published_at
                              )}`
                            : `Created ${formatDate(
                                article.created_at
                              )}`}
                        </span>

                      </div>

                    </div>


                    <div className="admin-article-actions">

                      <Link
                        to={`/admin/journal/edit/${article.id}`}
                        className="admin-edit-btn"
                      >
                        Edit
                      </Link>

                      <button
                        type="button"
                        onClick={() =>
                          handleTogglePublished(article)
                        }
                        disabled={actionLoading}
                        className="admin-publish-btn"
                      >
                        {article.published
                          ? "Unpublish"
                          : "Publish"}
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          handleDelete(article.id)
                        }
                        disabled={actionLoading}
                        className="admin-delete-btn"
                      >
                        Delete
                      </button>

                    </div>

                  </article>

                ))}

              </div>

            )}

          </section>

        </main>

      </div>

    </div>
  );
};

export default AdminJournal;